import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, Leaf, ShieldCheck, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { refresh } = useAuth();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (pw !== pw2) {
      toast.error("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reset. You're signed in.");
      if (typeof refresh === "function") {
        try { await refresh(); } catch (_e) { /* ignore */ }
      }
      navigate("/snap", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex justify-center" data-testid="reset-password-page">
      <div className="w-full max-w-md min-h-screen flex flex-col px-6 py-10 sm:px-8 sm:py-14">
        <div className="flex items-center gap-2.5">
          <span className="w-10 h-10 rounded-full bg-[#2B422F] flex items-center justify-center">
            <Leaf size={20} weight="fill" className="text-[#E6D4C4]" />
          </span>
          <div>
            <div className="font-serif-display text-2xl leading-none text-[#1A2421]">Tally</div>
            <div className="label-eyebrow mt-1">Carb Counter</div>
          </div>
        </div>

        <div className="mt-14">
          <span className="label-eyebrow">Choose a new password</span>
          <h1 className="font-serif-display text-4xl tracking-tight text-[#1A2421] mt-2">Set new password</h1>
          <p className="text-sm text-[#5C6B62] mt-2">At least 6 characters. You&apos;ll be signed in right after.</p>
        </div>

        {!token ? (
          <div className="mt-8 rounded-2xl bg-white border border-[#E6E3D8] p-5" data-testid="reset-password-missing-token">
            <div className="text-sm text-[#1A2421] font-semibold">Link is incomplete.</div>
            <p className="text-sm text-[#5C6B62] mt-1">
              This URL is missing the reset token. Please open the link from your email exactly as we sent it.
            </p>
            <Link
              to="/forgot-password"
              className="btn-outline w-full mt-4 inline-flex items-center justify-center gap-2 !py-2 text-sm"
            >
              Request a new link
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="reset-password-form">
            <div>
              <label className="label-eyebrow block mb-2">New password</label>
              <div className="relative">
                <input
                  className="input-organic pr-12"
                  type={showPw ? "text" : "password"}
                  required
                  minLength={6}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  data-testid="reset-password-new-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-[#5C6B62] hover:text-[#1A2421]"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="reset-password-toggle"
                  tabIndex={-1}
                >
                  {showPw ? <EyeSlash size={20} weight="regular" /> : <Eye size={20} weight="regular" />}
                </button>
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Confirm password</label>
              <input
                className="input-organic"
                type={showPw ? "text" : "password"}
                required
                minLength={6}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                placeholder="Re-enter password"
                autoComplete="new-password"
                data-testid="reset-password-confirm-input"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="reset-password-submit"
            >
              {submitting ? "Updating…" : "Update password"}
              <ArrowRight size={16} weight="bold" />
            </button>
          </form>
        )}

        <div className="mt-6 flex items-center gap-2 text-xs text-[#5C6B62]">
          <ShieldCheck size={14} weight="bold" /> Links expire 30 minutes after they&apos;re sent.
        </div>

        <div className="mt-auto pt-10 text-center">
          <Link to="/login" className="text-xs text-[#5C6B62] underline underline-offset-4 hover:text-[#1A2421]">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
